import React, { useEffect, useMemo, useState } from 'react';
import { ReceiptPercentIcon, PencilSquareIcon, PlusIcon } from '@heroicons/react/24/outline';
import Toggle from '../../components/Toggle';
import {
  additionalCostItemTypesService,
  type AdditionalCostItemTypeAdmin,
  type AdditionalCostItemTypeCreate,
} from '../../services/additionalCostItemTypes.service';
import ModalHeader from '../../components/ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';

/** Catalogue of cost-item types used by the V1 "Additional Cost Item" modal
 *  (vendor invoice context). Mirrors the Driver Pay Addition Types page but
 *  without variant/flag columns — cost items have a single form layout. */

type EditState =
  | { kind: 'closed' }
  | { kind: 'new' }
  | { kind: 'edit'; row: AdditionalCostItemTypeAdmin };

const emptyForm: AdditionalCostItemTypeCreate = {
  name: '',
  sortOrder: 0,
  isActive: true,
};

const AdditionalCostItemTypes: React.FC = () => {
  const [rows, setRows] = useState<AdditionalCostItemTypeAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditState>({ kind: 'closed' });
  const [showInactive, setShowInactive] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await additionalCostItemTypesService.list();
      setRows(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load cost item types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const visibleRows = useMemo(
    () => (showInactive ? rows : rows.filter((r) => r.isActive)),
    [rows, showInactive],
  );

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <ReceiptPercentIcon className="h-6 w-6 text-gray-500" />
          <h1 className="text-xl font-semibold text-heading">
            Additional Cost Item Types
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-label">
            <Toggle
              checked={showInactive}
              onChange={setShowInactive}
              size="sm"
              ariaLabel="Show inactive types"
            />
            Show inactive
          </label>
          <button
            type="button"
            onClick={() => setEdit({ kind: 'new' })}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 border border-indigo-600 rounded hover:bg-indigo-700"
          >
            <PlusIcon className="h-4 w-4" />
            Add type
          </button>
        </div>
      </div>

      <p className="mb-4 text-sm text-muted">
        These rows drive the Type dropdown on the V1-style “Additional Cost
        Item” modal (vendor invoices). Inactive types are hidden from the
        dropdown but kept so legacy rows in additionalcost.costtype still
        resolve.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}{' '}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : (
        <div className="bg-panel shadow rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <Th>Name</Th>
                <Th className="text-right">Sort</Th>
                <Th className="text-center">Active</Th>
                <Th className="w-12" />
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-gray-400 italic">
                    No cost item types found.
                  </td>
                </tr>
              ) : (
                visibleRows.map((r) => (
                  <tr
                    key={r.id}
                    className={
                      'border-t border-gray-100 hover:bg-highlight dark:border-gray-700' +
                      (r.isActive ? '' : 'opacity-60')
                    }
                  >
                    <Td className="font-medium">{r.name}</Td>
                    <Td className="text-right">{r.sortOrder}</Td>
                    <Td className="text-center">{r.isActive ? 'Yes' : 'No'}</Td>
                    <Td className="text-right">
                      <button
                        type="button"
                        onClick={() => setEdit({ kind: 'edit', row: r })}
                        className="text-indigo-600 hover:text-indigo-700"
                        aria-label={`Edit ${r.name}`}
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {edit.kind !== 'closed' && (
        <EditModal
          initial={edit.kind === 'edit' ? toForm(edit.row) : emptyForm}
          recordId={edit.kind === 'edit' ? edit.row.id : null}
          onClose={() => setEdit({ kind: 'closed' })}
          onSaved={async () => {
            setEdit({ kind: 'closed' });
            await load();
          }}
        />
      )}
    </div>
  );
};

const toForm = (r: AdditionalCostItemTypeAdmin): AdditionalCostItemTypeCreate => ({
  name: r.name,
  sortOrder: r.sortOrder,
  isActive: r.isActive,
});

const Th: React.FC<{ children?: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <th
    className={
      'px-3 py-2 text-left text-[0.6875rem] font-semibold uppercase tracking-wider ' +
      'text-gray-500 dark:text-gray-300 ' +
      (className ?? '')
    }
  >
    {children}
  </th>
);

const Td: React.FC<{ children?: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <td className={'px-3 py-2 text-body' + (className ?? '')}>{children}</td>
);

// ---------------------------------------------------------------------------
// Edit modal
// ---------------------------------------------------------------------------

interface EditModalProps {
  initial: AdditionalCostItemTypeCreate;
  recordId: number | null;
  onClose: () => void;
  onSaved: () => void;
}

const EditModal: React.FC<EditModalProps> = ({ initial, recordId, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  const field = <K extends keyof AdditionalCostItemTypeCreate>(
    key: K,
    value: AdditionalCostItemTypeCreate[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (recordId == null) {
        await additionalCostItemTypesService.create(form);
      } else {
        await additionalCostItemTypesService.update(recordId, form);
      }
      onSaved();
    } catch (err: unknown) {
      const detail =
        typeof err === 'object' && err && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      setError(detail || (err instanceof Error ? err.message : 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (recordId == null) return;
    if (!window.confirm('Deactivate this cost item type? It will be hidden from the modal.')) return;
    setSaving(true);
    setError(null);
    try {
      await additionalCostItemTypesService.deactivate(recordId);
      onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={onClose} />
        <div
          ref={panelRef}
          style={panelStyle}
          className="relative bg-panel rounded-lg shadow-xl max-w-md w-full"
        >
          <ModalHeader
            title={recordId == null ? 'New Cost Item Type' : 'Edit Cost Item Type'}
            onClose={onClose}
            dragHandleProps={dragHandleProps}
          />

          <div className="px-4 py-3 space-y-3">
            {error && (
              <div className="px-2 py-1 text-xs text-red-700 bg-red-50 border border-red-200 rounded">
                {error}
              </div>
            )}

            <Field label="Name">
              <input
                className={inputClass}
                value={form.name}
                onChange={(e) => field('name', e.target.value)}
                onBlur={(e) => field('name', e.target.value.toUpperCase())}
                placeholder="e.g. FREIGHT BILL"
                disabled={recordId != null}
              />
              {recordId != null && (
                <p className="mt-1 text-[0.6875rem] text-gray-500">
                  Name is locked to keep existing additionalcost rows resolvable.
                </p>
              )}
            </Field>

            <Field label="Sort order">
              <input
                type="number"
                className={inputClass}
                value={form.sortOrder}
                onChange={(e) => field('sortOrder', parseInt(e.target.value, 10) || 0)}
              />
            </Field>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-800 dark:text-gray-200">Active</span>
              <Toggle
                checked={form.isActive}
                onChange={(v) => field('isActive', v)}
                size="sm"
                ariaLabel="Active"
              />
            </div>
          </div>

          <div className="px-4 py-3 border-t border-slate-200 dark:border-gray-700 flex items-center justify-end gap-2">
            {recordId != null && form.isActive && (
              <button
                type="button"
                onClick={handleDeactivate}
                disabled={saving}
                className="mr-auto px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 disabled:opacity-50"
              >
                Deactivate
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-3 py-1.5 text-sm font-medium text-label bg-gray-50 border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-50 dark:bg-gray-700 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1.5 text-sm font-semibold text-white bg-indigo-600 border border-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const inputClass =
  'w-full h-8 px-2 text-sm rounded border border-slate-300 bg-white text-slate-900 ' +
  'focus:outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/20 disabled:bg-slate-100 disabled:text-slate-500 ' +
  'dark:bg-gray-700 dark:text-white dark:border-slate-600 dark:disabled:bg-gray-600';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-500 mb-1">
      {label}
    </label>
    {children}
  </div>
);

export default AdditionalCostItemTypes;
